import Seo from "@/components/Seo";
import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Check } from "lucide-react";
import { api, DONATE_URL, formatApiErrorDetail } from "@/lib/api";
import { RevealLines, FadeUp } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const TYPES = ["Sponsorship", "Editorial Partnership", "Community Partnership", "Grant / Foundation", "Other"];

const TIERS = [
  { name: "Community Sponsor", desc: "Underwrite a single reporting project or Lift Every Voice session." },
  { name: "Editorial Partner", desc: "Co-produce solutions-centered series with shared distribution." },
  { name: "Founding Backer", desc: "Sustain the newsroom's independent, community-first model." },
];

export default function Partner() {
  const [form, setForm] = useState({
    org_name: "", contact_name: "", email: "", phone: "",
    partnership_type: "Sponsorship", message: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/inquiries", form);
      setDone(true);
      toast.success("Inquiry received", { description: "Our team will be in touch soon." });
      setForm({ org_name: "", contact_name: "", email: "", phone: "", partnership_type: "Sponsorship", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send inquiry");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="partner-page">
      <Seo title="Partner & Sponsor" description="Sponsors and partners make community-centered journalism possible. Start the conversation with The Canary Report." />
      <section className="pt-[120px] pb-16 max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12">
        <p className="label text-ink/50 mb-6">Partner &amp; Sponsor</p>
        <RevealLines as="h1"
          className="font-display font-black text-[12vw] lg:text-[7vw] leading-[0.86] tracking-tight"
          lines={["Build the", "newsroom", "with us."]}
        />
        <FadeUp delay={0.7}>
          <p className="mt-8 max-w-2xl text-lg text-ink/70 leading-relaxed">
            Sponsors and partners make community-centered journalism possible. Prefer to give
            directly? You can also{" "}
            <a href={DONATE_URL} target="_blank" rel="noopener noreferrer" data-testid="partner-donate-inline" className="text-ink font-semibold link-underline pb-0.5">
              donate here
            </a>.
          </p>
        </FadeUp>
      </section>

      <section className="bg-paper py-20 lg:py-28">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-6">
          {TIERS.map((t, i) => (
            <FadeUp key={t.name} delay={i * 0.08} className="lg:col-span-4">
              <div className="bg-white border border-ink p-8 h-full flex flex-col justify-between min-h-[240px]">
                <span className="font-display text-4xl font-black text-canary">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <h3 className="font-display text-2xl font-bold mb-3">{t.name}</h3>
                  <p className="text-ink/70 leading-relaxed">{t.desc}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      <section className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 py-24 grid lg:grid-cols-12 gap-12">
        <FadeUp className="lg:col-span-5">
          <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight leading-[0.95]">Start the conversation.</h2>
          <p className="mt-6 text-lg text-ink/70 leading-relaxed">
            Tell us about your organization and how you'd like to collaborate. Every inquiry
            reaches our editorial and partnerships team directly.
          </p>
        </FadeUp>

        <div className="lg:col-span-6 lg:col-start-7">
          {done ? (
            <div data-testid="inquiry-success" className="border border-ink p-10 flex flex-col items-start gap-4">
              <span className="h-12 w-12 rounded-full bg-canary grid place-items-center"><Check /></span>
              <h3 className="font-display text-3xl font-bold">Thank you.</h3>
              <p className="text-ink/70">Your inquiry has been received. We'll respond shortly.</p>
              <button data-testid="inquiry-another-btn" onClick={() => setDone(false)} className="label link-underline pb-1 mt-2">Send another</button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="inquiry-form" className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <Label className="label" htmlFor="org">Organization *</Label>
                  <Input id="org" required value={form.org_name} onChange={set("org_name")} data-testid="inquiry-org" className="mt-2 rounded-none border-ink h-12" placeholder="Organization name" />
                </div>
                <div>
                  <Label className="label" htmlFor="contact">Your name *</Label>
                  <Input id="contact" required value={form.contact_name} onChange={set("contact_name")} data-testid="inquiry-contact" className="mt-2 rounded-none border-ink h-12" placeholder="Full name" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <Label className="label" htmlFor="email">Email *</Label>
                  <Input id="email" type="email" required value={form.email} onChange={set("email")} data-testid="inquiry-email" className="mt-2 rounded-none border-ink h-12" placeholder="you@org.com" />
                </div>
                <div>
                  <Label className="label" htmlFor="phone">Phone</Label>
                  <Input id="phone" value={form.phone} onChange={set("phone")} data-testid="inquiry-phone" className="mt-2 rounded-none border-ink h-12" placeholder="(optional)" />
                </div>
              </div>
              <div>
                <Label className="label">Partnership type</Label>
                <Select value={form.partnership_type} onValueChange={(v) => setForm((f) => ({ ...f, partnership_type: v }))}>
                  <SelectTrigger data-testid="inquiry-type" className="mt-2 rounded-none border-ink h-12"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TYPES.map((t) => <SelectItem key={t} value={t} data-testid={`type-${t}`}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label" htmlFor="message">Message *</Label>
                <Textarea id="message" required rows={5} value={form.message} onChange={set("message")} data-testid="inquiry-message" className="mt-2 rounded-none border-ink" placeholder="How would you like to collaborate?" />
              </div>
              <Button type="submit" disabled={sending} data-testid="inquiry-submit" className="rounded-full bg-ink text-white hover:bg-canary hover:text-ink h-14 px-9 label">
                {sending ? "Sending…" : <>Send inquiry <ArrowUpRight size={16} className="ml-2" /></>}
              </Button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
